import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  Container,
  Paper,
  Grid,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { ArrowBack, Check, Home } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  createOpportunity,
  updateOpportunity,
  fetchOpportunityById,
} from '../../redux/slices/opportunitySlice';
import { fetchCustomers } from '../../redux/slices/customerSlice';
import styles from './OpportunityFormPage.module.css';
import BackButton from '../../components/BackButton/BackButton';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { CreateOpportunityRequest } from '../../services/opportunity-service';
import { isAuthenticated } from '../../services/auth-service';
import { Opportunity, Customer } from '../../types';

interface FormData {
  customer_id: string;
  title: string;
  description: string;
  status: 'Open' | 'Won' | 'Lost';
  estimated_closing_date: string;
  value: string;
  source: string;
  products: {
    notebooks: boolean;
    chromebooks: boolean;
    miniPCs: boolean;
    monitors: boolean;
    accessories: boolean;
    services: boolean;
  };
}

const OpportunityFormPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const { setBreadcrumbs } = useBreadcrumb();
  const isEditMode = !!id;

  const { selectedOpportunity, loading, error } = useAppSelector(state => state.opportunities);
  const { items: customers, loading: customersLoading } = useAppSelector(state => state.customers);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [formData, setFormData] = useState<FormData>({
    customer_id: '',
    title: '',
    description: '',
    value: '',
    estimated_closing_date: '',
    status: 'Open',
    source: 'Website',
    products: {
      notebooks: false,
      chromebooks: false,
      miniPCs: false,
      monitors: false,
      accessories: false,
      services: false,
    },
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Fetch opportunity data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchOpportunityById(id));
    }
  }, [dispatch, id, isEditMode]);

  // Fetch customers data
  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  // Set form data when opportunity is loaded
  useEffect(() => {
    if (isEditMode && selectedOpportunity) {
      setFormData(prev => ({
        ...prev,
        customer_id: selectedOpportunity.customer_id || '',
        title: selectedOpportunity.title || '',
        description: selectedOpportunity.description || '',
        status: selectedOpportunity.status || 'Open',
        estimated_closing_date: selectedOpportunity.estimated_closing_date || '',
        value: selectedOpportunity.value?.toString() || '',
        source: selectedOpportunity.source || 'Website',
        products: {
          ...prev.products,
          notebooks: selectedOpportunity.products_services?.includes('Notebook') || false,
          chromebooks: selectedOpportunity.products_services?.includes('Chromebooks') || false,
          miniPCs: selectedOpportunity.products_services?.includes('MiniPCs') || false,
          monitors: selectedOpportunity.products_services?.includes('Monitores') || false,
          accessories: selectedOpportunity.products_services?.includes('Acessórios') || false,
          services: selectedOpportunity.products_services?.includes('Serviços') || false,
        },
      }));
    }
  }, [isEditMode, selectedOpportunity]);

  // Set breadcrumbs
  useEffect(() => {
    if (isEditMode && selectedOpportunity) {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
        { label: 'Oportunidades', path: '/opportunities' },
        { label: selectedOpportunity.title, path: `/opportunities/${id}` },
        { label: 'Editar' },
      ]);
    } else {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
        { label: 'Oportunidades', path: '/opportunities' },
        { label: 'Nova Oportunidade' },
      ]);
    }

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> }]);
    };
  }, [isEditMode, selectedOpportunity, id, setBreadcrumbs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when field is edited
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      products: {
        ...prev.products,
        [name]: checked,
      },
    }));
  };

  const handleStatusChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.value as 'Open' | 'Won' | 'Lost',
    }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'Cliente é obrigatório';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!formData.value || formData.value.trim() === '') {
      newErrors.value = 'Valor estimado é obrigatório';
    } else if (isNaN(Number(formData.value))) {
      newErrors.value = 'Valor estimado deve ser um número';
    }

    if (!formData.estimated_closing_date.trim()) {
      newErrors.estimated_closing_date = 'Data estimada é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Convert the product checkboxes to an array of product names
    const productsArray: string[] = [];
    if (formData.products.notebooks) productsArray.push('Notebook');
    if (formData.products.chromebooks) productsArray.push('Chromebooks');
    if (formData.products.miniPCs) productsArray.push('MiniPCs');
    if (formData.products.monitors) productsArray.push('Monitores');
    if (formData.products.accessories) productsArray.push('Acessórios');
    if (formData.products.services) productsArray.push('Serviços');

    // Prepare data for API
    const opportunityData: CreateOpportunityRequest = {
      customer_id: formData.customer_id,
      title: formData.title,
      description: formData.description,
      status: formData.status,
      estimated_closing_date: formData.estimated_closing_date,
      value: parseFloat(formData.value),
      source: formData.source,
      products_services: productsArray,
    };

    try {
      // Create or update opportunity
      if (isEditMode && id) {
        await dispatch(updateOpportunity({ id, data: opportunityData })).unwrap();
        setSnackbarMessage('Oportunidade atualizada com sucesso!');
      } else {
        await dispatch(createOpportunity(opportunityData)).unwrap();
        setSnackbarMessage('Oportunidade criada com sucesso!');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Navigate back to opportunities list after a short delay
      setTimeout(() => {
        navigate('/opportunities');
      }, 1500);
    } catch (error) {
      setSnackbarMessage('Erro ao salvar oportunidade. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSaveDraft = () => {
    // For draft, we'll use the same submit but with status Draft
    // If your API supports draft status, you can modify this accordingly
    setFormData(prev => ({ ...prev, status: 'Open' }));
    // Create a mock event with preventDefault to avoid the ESLint warning
    const mockEvent = {
      preventDefault: () => {
        // Prevent default form submission behavior
        console.log('Preventing default form submission for draft save');
      },
    };
    handleSubmit(mockEvent as React.FormEvent);
  };

  const handleCancel = () => {
    navigate('/opportunities');
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading && isEditMode) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <BackButton />

      <Box>
        <Typography variant="h5">Acelere suas vendas com o Registro de Oportunidades!</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Registre novas oportunidades de negócios para os produtos da Acer Empresas e potencialize
          o sucesso das suas vendas. Preencha o formulário abaixo e garanta condições especiais para
          seu cliente.
        </Typography>
      </Box>

      <Paper className={styles.formContainer}>
        <Typography variant="h6" className={styles.sectionTitle}>
          Informações da Oportunidade
        </Typography>

        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <FormControl fullWidth error={!!errors.customer_id} className={styles.fullWidth}>
              <InputLabel id="customer-select-label">Cliente</InputLabel>
              <Select
                labelId="customer-select-label"
                id="customer-select"
                name="customer_id"
                value={formData.customer_id}
                label="Cliente"
                onChange={e => {
                  setFormData(prev => ({
                    ...prev,
                    customer_id: e.target.value as string,
                  }));
                }}
              >
                <MenuItem value="" disabled>
                  Selecione um cliente
                </MenuItem>
                {customersLoading ? (
                  <MenuItem disabled>Carregando clientes...</MenuItem>
                ) : (
                  customers.map(customer => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.customer_id && <FormHelperText>{errors.customer_id}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              label="Título do Projeto"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              error={!!errors.title}
              helperText={errors.title}
            />

            <TextField
              fullWidth
              label="Descrição da Oportunidade"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description}
            />

            <TextField
              fullWidth
              label="Valor Estimado do Projeto (R$)"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              error={!!errors.value}
              helperText={errors.value}
            />

            <TextField
              fullWidth
              label="Data Estimada de Fechamento"
              name="estimated_closing_date"
              type="date"
              value={formData.estimated_closing_date}
              onChange={handleInputChange}
              error={!!errors.estimated_closing_date}
              helperText={errors.estimated_closing_date}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <Typography variant="h6" className={styles.sectionTitle}>
            Produtos/Serviços Ofertados
          </Typography>
          <Typography color="textSecondary" gutterBottom>
            Selecione os produtos e serviços que fazem parte dessa oportunidade
          </Typography>

          <div className={styles.checkboxGrid}>
            <FormControlLabel
              control={
                <Checkbox
                  name="notebooks"
                  checked={formData.products.notebooks}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="Notebook"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="chromebooks"
                  checked={formData.products.chromebooks}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="Chromebooks"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="miniPCs"
                  checked={formData.products.miniPCs}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="MiniPCs"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="monitors"
                  checked={formData.products.monitors}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="Monitores"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="accessories"
                  checked={formData.products.accessories}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="Acessórios"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="services"
                  checked={formData.products.services}
                  onChange={handleProductChange}
                  color="primary"
                />
              }
              label="Serviços"
            />
          </div>

          <div className={styles.benefitsSection}>
            <div className={styles.checkIcon}>
              <Check fontSize="medium" sx={{ fontSize: '18px', color: '#1E4620' }} />
            </div>
            <div className={styles.benefitHeader}>
              <Typography variant="h6" fontWeight="500">
                Benefícios e Condições da Oportunidade
              </Typography>
            </div>
            <Typography color="textSecondary" sx={{ mb: 1 }}>
              Ao registrar essa oportunidade, você pode garantir:
            </Typography>
            <div className={styles.benefitsList}>
              <div className={styles.benefitItem}>
                <Check fontSize="small" sx={{ color: '#1E4620', mr: 1 }} />
                <Typography color="textSecondary">
                  Condições comerciais exclusivas para seu cliente.
                </Typography>
              </div>
              <div className={styles.benefitItem}>
                <Check fontSize="small" sx={{ color: '#1E4620', mr: 1 }} />
                <Typography color="textSecondary">
                  Prioridade na negociação e suporte especializado da Acer.
                </Typography>
              </div>
              <div className={styles.benefitItem}>
                <Check fontSize="small" sx={{ color: '#1E4620', mr: 1 }} />
                <Typography color="textSecondary">
                  Proteção da oportunidade por um período exclusivo para sua empresa.
                </Typography>
              </div>
              <div className={styles.benefitItem}>
                <Check fontSize="small" sx={{ color: '#1E4620', mr: 1 }} />
                <Typography color="textSecondary">
                  Acompanhamento do status da negociação diretamente no painel de oportunidades.
                </Typography>
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <Button variant="outlined" color="inherit" onClick={handleCancel}>
              Cancelar
            </Button>
            {/*
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleSaveDraft}
              data-action="draft"
              disabled={loading}
            >
              Salvar como Rascunho
            </Button>
            */}
            <Button variant="contained" color="primary" type="submit" disabled={loading}>
              {loading ? (
                <CircularProgress size={24} />
              ) : isEditMode ? (
                'Atualizar Oportunidade'
              ) : (
                'Registrar Oportunidade'
              )}
            </Button>
          </div>
        </form>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OpportunityFormPage;
